import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { supabase } from "../supabaseClient";

export default function ImportCollectionModal({ show, onClose, user }) {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [resultMessage, setResultMessage] = useState(null);

  // ---------------- LOAD CATEGORIES ----------------
  useEffect(() => {
    if (!show) return;

    const load = async () => {
      const { data: cats } = await supabase.from("categories").select("*");
      const { data: subs } = await supabase.from("subcategories").select("*");
      setCategories(cats || []);
      setSubcategories(subs || []);
    };

    load();
  }, [show]);

  if (!show) return null;

  // ---------------- NORMALIZE & MAP ----------------
  const normalize = (str) => str?.toString().trim().toLowerCase();

  const categoryMap = Object.fromEntries(
    categories.map(c => [normalize(c.name), c.id])
  );

  const subcategoryMap = Object.fromEntries(
    subcategories.map(s => [normalize(s.name), s.id])
  );

  // ---------------- FILE CHANGE ----------------
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setValidationErrors([]);
    setResultMessage(null);
  };

  // ---------------- VALIDATION ----------------
  const validateFile = async () => {
    if (!file) return { valid: [], ok: false };

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer);

    const sheet = workbook.Sheets["Import Template"];
    if (!sheet) {
      setValidationErrors(["Missing 'Import Template' sheet."]);
      return { valid: [], ok: false };
    }

    const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
    console.log("Parsed rows:", rows);

    const required = ["Name", "Category", "Subcategory"];
    const errors = [];
    const valid = [];

    rows.forEach((row, index) => {
      const rowNum = index + 2;

      // Skip empty rows
      if (
        !row.Name &&
        !row.Brand &&
        !row.Set &&
        !row.Category &&
        !row.Subcategory
      ) return;

      // Check for missing required fields
      const missing = required.filter(c => !row[c] || row[c].toString().trim() === "");
      if (missing.length) {
        errors.push(`Row ${rowNum}: Missing ${missing.join(", ")}`);
        return;
      }

      const category_id = categoryMap[normalize(row.Category)];
      const subcategory_id = subcategoryMap[normalize(row.Subcategory)];

      if (!category_id || !subcategory_id) {
        errors.push(
          `Row ${rowNum}: Invalid Category/Subcategory -> "${row.Category}" / "${row.Subcategory}"`
        );
        console.log(`Row ${rowNum} mismatch: category_id=${category_id}, subcategory_id=${subcategory_id}`);
        return;
      }

      valid.push({
        name: row.Name || null,
        brand: row.Brand || null,
        set: row.Set || null,
        year: row.Year || null,
        category_id,
        subcategory_id,
        condition: row.Condition || null,
        price_paid: row["Price Paid"] || null,
        notes: row.Notes || null,
        card_number: row.Card_Number || null,
      });
    });

    setValidationErrors(errors);
    console.log("Valid rows ready for RPC:", valid);

    return { valid, ok: valid.length > 0 && errors.length === 0 };
  };

  // ---------------- UPLOAD (BATCH) ----------------
  const uploadFile = async () => {
    const { valid, ok } = await validateFile();
    if (!ok) {
      setResultMessage({ type: "error", text: "Validation failed. Fix errors and try again." });
      return;
    }

    setUploading(true);
    setResultMessage(null);

    console.log("Sending rows to RPC:", valid);

    try {
      const { data, error } = await supabase.rpc("batch_add_items", {
        p_user_id: user.id,
        p_rows: valid
      });

      if (error) throw error;

      setResultMessage({
        type: "success",
        text: `Successfully imported ${data} item(s).`
      });
    } catch (err) {
      console.error("Batch import failed:", err);
      setResultMessage({ type: "error", text: err.message });
    } finally {
      setUploading(false);
    }
  };

  // ---------------- DOWNLOAD TEMPLATE ----------------
    const downloadTemplate = () => {
      // -------- Import Template Sheet --------
      const templateRows = [
        {
          Name: "",
          Brand: "",
          Set: "",
          Card_Number: "",
          Year: "",
          Category: "",
          Subcategory: "",
          Condition: "",
          "Price Paid": "",
          Notes: ""
        }
      ];

      const wsTemplate = XLSX.utils.json_to_sheet(templateRows);

      // -------- Instructions Sheet --------
      const instructionsRows = [
        { Step: "Welcome to Hobbli Import!" },
        { Step: "" },
        { Step: "1. Fill in the 'Import Template' sheet." },
        { Step: "2. Required columns: Name, Category, Subcategory." },
        { Step: "3. Optional columns: Brand, Set, Year, Condition, Price Paid, Notes." },
        { Step: "4. Category and Subcategory must match the 'Categories Reference' sheet." },
        { Step: "   - Capitalization does not matter." },
        { Step: "   - Spelling must match exactly." },
        { Step: "5. Leave unused cells blank." },
        { Step: "6. Do not rename columns or sheets." },
        { Step: "7. Save the file and upload it back into Hobbli." },
        { Step: "" },
        { Step: "Categories & Subcategories:" },
        ...categories.map(c => ({
          Step: `${c.name}: ${subcategories
            .filter(s => s.category_id === c.id)
            .map(s => s.name)
            .join(", ")}`
        }))
      ];

      const wsInstructions = XLSX.utils.json_to_sheet(instructionsRows);

      // -------- Categories Reference Sheet --------
      const referenceRows = categories.map(c => ({
        Category: c.name,
        Subcategories: subcategories
          .filter(s => s.category_id === c.id)
          .map(s => s.name)
          .join(", ")
      }));

      const wsReference = XLSX.utils.json_to_sheet(referenceRows);

      // -------- Workbook --------
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");
      XLSX.utils.book_append_sheet(wb, wsTemplate, "Import Template");
      XLSX.utils.book_append_sheet(wb, wsReference, "Categories Reference");

      XLSX.writeFile(wb, "hobbli-import-template.xlsx");
    };



  // ---------------- UI ----------------
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: "600px", padding: "1.5rem" }}
      >
        <h3>Import Collection Items (Excel)</h3>

       <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} />

          <button
            type="button"
            onClick={downloadTemplate}
          >
            Download Excel Template
          </button>

          <button
            onClick={uploadFile}
            disabled={uploading || !file}
          >
            {uploading ? "Importing…" : "Validate & Import"}
          </button>

          <button className="btn-danger" onClick={onClose}>
            Close
          </button>
          <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
            Tip: Download the Excel template for instructions and a full category reference.
          </p>

        </div>

        {validationErrors.length > 0 && (
          <div style={{ color: "orange", marginTop: "1rem" }}>
            <strong>Validation Errors</strong>
            <ul>
              {validationErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {resultMessage && (
          <div
            style={{
              marginTop: "1rem",
              color: resultMessage.type === "success" ? "green" : "red"
            }}
          >
            <strong>{resultMessage.text}</strong>
          </div>
        )}
      </div>
    </div>
  );
}
